import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import type { GetToken } from "./eqmsApi";
import type { WorkAreaType } from "../models/eqms";

/**
 * The eQMS module's top-level navigation section. The main trio (dashboard/sprint/documents) is the
 * project-wide roll-up; when a work area is selected the sprint/documents/design/test sections scope
 * to that area. inbox/external/admin are cross-cutting.
 */
export type EqmsSection =
  | "dashboard"
  | "sprint"
  | "workarea"
  | "documents"
  | "versions"
  | "design"
  | "test"
  | "inbox"
  | "external"
  | "trace"
  | "risk"
  | "attachments"
  | "admin";

/** The context lens on the top bar: iterate by sprint, or view by release version. */
export type EqmsLens = "sprint" | "version";

/**
 * Selection + navigation state for the eQMS module. Screens receive this controller and call the
 * eqmsApi themselves (passing getToken) for their own data, mirroring how IssuesView drives itself.
 * Workspace/project selection is delegated up (the shell already owns workspaces); this controller
 * holds the active workspaceId + projectId once chosen, plus the nav section, active work area, and
 * the sprint/version context lens.
 */
export function useEqms() {
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken: GetToken = useMemo(() => () => tokenRef.current(), []);

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);

  const [section, setSection] = useState<EqmsSection>("dashboard");
  /** null = project-wide roll-up; a value scopes documents/sprint/design/test to that work area. */
  const [workArea, setWorkArea] = useState<WorkAreaType | null>(null);

  const [lens, setLens] = useState<EqmsLens>("sprint");
  const [contextVersionId, setContextVersionId] = useState<string | null>(null);
  const [contextSprintId, setContextSprintId] = useState<string | null>(null);

  // A detail target within a section (e.g. an open document id). Screens interpret it.
  const [detailId, setDetailId] = useState<string | null>(null);

  // Selecting a project resets the in-project nav to the dashboard roll-up.
  const selectProject = useCallback((pid: string | null) => {
    setProjectId(pid);
    setSection("dashboard");
    setWorkArea(null);
    setDetailId(null);
  }, []);

  const selectWorkspace = useCallback((wid: string | null) => {
    setWorkspaceId(wid);
    setProjectId(null);
    setSection("dashboard");
    setWorkArea(null);
    setDetailId(null);
  }, []);

  /** Navigate to a section; optionally within a work area. Clears any open detail. */
  const goTo = useCallback((next: EqmsSection, area: WorkAreaType | null = null) => {
    setSection(next);
    setWorkArea(area);
    setDetailId(null);
  }, []);

  const openDetail = useCallback((next: EqmsSection, id: string, area: WorkAreaType | null = null) => {
    setSection(next);
    setWorkArea(area);
    setDetailId(id);
  }, []);

  // Clear the open document/detail when leaving the documents section.
  useEffect(() => {
    if (section !== "documents") return;
  }, [section]);

  return {
    getToken,
    workspaceId,
    projectId,
    section,
    workArea,
    lens,
    contextVersionId,
    contextSprintId,
    detailId,
    selectWorkspace,
    selectProject,
    goTo,
    openDetail,
    setWorkArea,
    setLens,
    setContextVersionId,
    setContextSprintId,
    setDetailId,
  };
}

export type EqmsController = ReturnType<typeof useEqms>;
